// Invitation accept (TEAM-001): /invitations/{token}. Sign-in comes first (the server binds the
// invitation to the invited email or phone), then the platform terms (ORG-03), then one explicit
// Accept. GET /invitations/{token} names the business, role and inviter first, and tells a used,
// expired or someone-else's link apart before any click. Success refreshes /me and opens the
// business's first store on Today; a business without a store says so.
import { useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import { Button, ErrorState, SkeletonText, StatePanel } from "@ady/ui";
import { ApiError, api } from "../api/client";
import { errorMessage, isUnauthenticated } from "../api/errors";
import { keys, useMe } from "../api/queries";
import type { InvitationPreview, MeResponse } from "../api/types";
import { AuthLayout } from "../auth/AuthLayout";
import { LegalGate } from "../auth/LegalGate";
import { useGo } from "../shell/AppLink";
import { rememberStore } from "../shell/context";
import { TID } from "../testids";

type Failure = { title: string; body: string; requestId?: string };

function failureFor(err: unknown): Failure {
  const code = err instanceof ApiError ? err.code : undefined;
  const requestId = err instanceof ApiError ? err.requestId : undefined;
  if (code === "validation.invitation_used")
    return { title: t("invitation.errors.usedTitle"), body: t("invitation.errors.usedBody") };
  if (code === "validation.invitation_expired")
    return { title: t("invitation.errors.expiredTitle"), body: t("invitation.errors.expiredBody") };
  if (code === "validation.already_member")
    return { title: t("invitation.errors.memberTitle"), body: t("invitation.errors.memberBody") };
  if (code === "authz.forbidden")
    return {
      title: t("invitation.errors.mismatchTitle"),
      body: t("invitation.errors.mismatchBody"),
    };
  if (code === "not_found")
    return {
      title: t("invitation.errors.notFoundTitle"),
      body: t("invitation.errors.notFoundBody"),
    };
  return { title: t("states.errorTitle"), body: errorMessage(err), requestId };
}

/** A preview that can't be accepted becomes the matching failure (no Accept button). */
function previewFailure(p: InvitationPreview): Failure | null {
  if (p.state === "accepted")
    return { title: t("invitation.errors.usedTitle"), body: t("invitation.errors.usedBody") };
  if (p.state === "expired")
    return { title: t("invitation.errors.expiredTitle"), body: t("invitation.errors.expiredBody") };
  if (!p.for_you)
    return {
      title: t("invitation.errors.mismatchTitle"),
      body: t("invitation.errors.mismatchBody"),
    };
  return null;
}

type Outcome =
  { kind: "idle" } | { kind: "failed"; failure: Failure } | { kind: "no-store"; orgName: string };

function Loading() {
  return (
    <AuthLayout title={t("invitation.title")} testId={TID.invitationPage}>
      <div aria-busy="true" data-testid={TID.loadingState}>
        <span className="ady-sr-only">{t("states.loadingRegion")}</span>
        <SkeletonText lines={3} />
      </div>
    </AuthLayout>
  );
}

export function AcceptInvitationPage() {
  const { token } = useParams({ from: "/invitations/$token" });
  const me = useMe();
  const go = useGo();
  const unauth = me.isError && isUnauthenticated(me.error);
  useEffect(() => {
    if (unauth) go(`/sign-in?next=${encodeURIComponent(`/invitations/${token}`)}`);
  }, [unauth, go, token]);

  if (me.isPending || unauth) return <Loading />;
  if (me.isError) {
    return (
      <AuthLayout title={t("invitation.title")} testId={TID.invitationPage}>
        <ErrorState
          size="inline"
          body={errorMessage(me.error)}
          onRetry={() => void me.refetch()}
          data-testid={TID.errorState}
        />
      </AuthLayout>
    );
  }
  return (
    <LegalGate>
      <AcceptInvitation token={token} me={me.data} />
    </LegalGate>
  );
}

function AcceptInvitation({ token, me }: { token: string; me: MeResponse }) {
  const qc = useQueryClient();
  const go = useGo();
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>({ kind: "idle" });
  const preview = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api.invitationPreview(token),
    retry: false,
  });

  const accept = async () => {
    setBusy(true);
    try {
      const res = await api.acceptInvitation(token);
      void qc.invalidateQueries({ queryKey: ["invitation", token] });
      // staleTime 0: the cached /me predates the new membership and must not be reused.
      const fresh = await qc.fetchQuery({
        queryKey: keys.me,
        queryFn: () => api.me(),
        staleTime: 0,
      });
      const m = fresh.memberships.find((x) => x.org.id === res.org_id);
      const store = m?.stores[0];
      if (m && store) {
        rememberStore(m.org.slug, store.slug);
        go(`/${m.org.slug}/${store.slug}/today`);
        return;
      }
      setOutcome({ kind: "no-store", orgName: m?.org.name ?? "" });
    } catch (err) {
      setOutcome({ kind: "failed", failure: failureFor(err) });
    } finally {
      setBusy(false);
    }
  };

  const switchAccount = async () => {
    try {
      await api.logout();
    } finally {
      qc.clear();
      go(`/sign-in?next=${encodeURIComponent(`/invitations/${token}`)}`);
    }
  };

  if (preview.isPending && outcome.kind === "idle") return <Loading />;

  const p = preview.data;
  const blocked: Failure | null =
    outcome.kind === "failed"
      ? outcome.failure
      : outcome.kind === "no-store"
        ? null
        : preview.isError
          ? failureFor(preview.error)
          : p
            ? previewFailure(p)
            : null;
  const idle = outcome.kind === "idle" && !blocked;
  const who = me.user.email ?? me.user.phone ?? me.user.display_name;

  return (
    <AuthLayout
      title={t("invitation.title")}
      lead={idle ? t("invitation.lead") : undefined}
      testId={TID.invitationPage}
    >
      {blocked ? (
        <ErrorState
          size="inline"
          title={blocked.title}
          body={blocked.body}
          requestId={blocked.requestId}
          data-testid={TID.errorState}
        />
      ) : outcome.kind === "no-store" ? (
        <StatePanel
          kind="not-set-up"
          size="inline"
          title={t("invitation.noStoreTitle", { org: outcome.orgName })}
          body={t("invitation.noStoreBody")}
        />
      ) : (
        <>
          {p ? (
            <p className="text-md" data-testid={TID.invitationOffer}>
              {p.invited_by.display_name
                ? t("invitation.offer", {
                    inviter: p.invited_by.display_name,
                    org: p.org.name,
                    role: p.role.name,
                  })
                : t("invitation.offerNoInviter", { org: p.org.name, role: p.role.name })}
            </p>
          ) : null}
          <p className="text-md">{t("invitation.signedInAs", { email: who })}</p>
          <p className="text-sm text-fg-secondary">{t("invitation.rule")}</p>
          <Button
            icon="check"
            fullWidth
            loading={busy}
            loadingLabel={t("invitation.accepting")}
            onClick={accept}
            data-testid={TID.invitationAccept}
          >
            {t("invitation.accept")}
          </Button>
        </>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {idle ? null : (
          <Button variant="link" onClick={() => go("/")}>
            {t("invitation.openAdyvance")}
          </Button>
        )}
        <Button icon="swap" variant="link" onClick={switchAccount}>
          {t("invitation.switchAccount")}
        </Button>
      </div>
    </AuthLayout>
  );
}
